import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { StaffClockDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Dialog, EmptyState, Field, Input, useToast } from '@bifeai/ui';
import { Clock, LogIn, LogOut } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });

/**
 * Asistencia en el mostrador: el empleado toca su nombre, pone su PIN y queda marcada la entrada
 * (o la salida, si ya había entrado hoy).
 */
export function ClockDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const staff = useQuery({ queryKey: ['attendance', 'staff'], queryFn: () => api.get<StaffClockDTO[]>('/attendance/staff'), enabled: open });
  const [who, setWho] = useState<StaffClockDTO | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function close() {
    setWho(null);
    setPin('');
    setError(null);
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!who) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ name: string; type: 'IN' | 'OUT'; at: string }>('/attendance', { employeeId: who.id, pin });
      toast.success(
        r.type === 'IN' ? `¡Buen día, ${r.name.split(' ')[0]}!` : `Hasta mañana, ${r.name.split(' ')[0]}`,
        `${r.type === 'IN' ? 'Entrada' : 'Salida'} a las ${timeOf(r.at)}`,
      );
      await qc.invalidateQueries({ queryKey: ['attendance'] });
      await qc.invalidateQueries({ queryKey: ['employees'] });
      close();
    } catch (err) {
      setPin('');
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && !busy && close()}
      title={who ? who.name : 'Entrada y salida'}
      description={who ? `Poné tu PIN para marcar la ${who.status === 'IN' ? 'salida' : 'entrada'}.` : 'Tocá tu nombre.'}
    >
      {!who ? (
        (staff.data?.length ?? 0) === 0 ? (
          <EmptyState icon={<Clock />} title="No hay empleados con PIN" text="El dueño les pone el PIN en Empleados." />
        ) : (
          <div className="bf-clock-grid">
            {staff.data!.map((s) => (
              <button key={s.id} type="button" className="bf-clock-btn" onClick={() => setWho(s)}>
                <strong>{s.name}</strong>
                {s.status === 'IN' ? (
                  <Badge tone="success">adentro{s.since ? ` desde ${timeOf(s.since)}` : ''}</Badge>
                ) : s.status === 'OUT' ? (
                  <Badge>se fue{s.since ? ` ${timeOf(s.since)}` : ''}</Badge>
                ) : (
                  <span className="bf-cell-sub">sin marcar hoy</span>
                )}
              </button>
            ))}
          </div>
        )
      ) : (
        <form className="bf-form" onSubmit={submit} noValidate>
          <Field label="PIN">
            <Input
              className="bf-input--lg"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
            />
          </Field>
          {error && <Callout tone="danger">{error}</Callout>}
          <div className="bf-form-actions">
            <Button onClick={() => setWho(null)} disabled={busy}>
              Volver
            </Button>
            <Button type="submit" variant="primary" loading={busy} disabled={pin.length < 4} icon={who.status === 'IN' ? <LogOut /> : <LogIn />}>
              {who.status === 'IN' ? 'Marcar salida' : 'Marcar entrada'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
